import { Mail, Phone, MapPin, PhoneCall, CalendarClock, Instagram } from "lucide-react";
import WhatsAppIcon from "@/components/site/WhatsAppIcon";
import { CONTACT_INFO } from "@/data/content";

/**
 * "Contact Our Team" — a modern, mobile-first section that lists every way
 * to reach Maqam. Ottawa and Toronto are regional contacts, not offices.
 */
function scrollToForm(testid) {
  const el = document.querySelector(`[data-testid="${testid}"]`);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const input = el.querySelector("input, textarea, select");
    if (input) setTimeout(() => input.focus({ preventScroll: true }), 500);
  }
}

export default function ContactOurTeam() {
  return (
    <section
      data-testid="contact-our-team"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl">
          <span className="overline">Contact</span>
          <h2 className="mt-3 font-serif text-4xl md:text-5xl text-[color:var(--maqam-navy)] leading-[1.05] tracking-tight">
            Contact Our Team
          </h2>
          <p className="mt-5 text-[var(--maqam-muted)] leading-relaxed">
            Choose the option that suits you best. Our team responds quickly, and international
            travellers are welcome to reach out via WhatsApp.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[var(--maqam-muted)]">
            <MapPin size={16} className="text-[color:var(--maqam-gold)]" /> {CONTACT_INFO.location}
          </p>
        </div>

        {/* Regional phone cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {CONTACT_INFO.regional.map((r, i) => {
            const testid = i === 0 ? "regional-ottawa" : "regional-toronto";
            const label = i === 0 ? "Call Ottawa" : "Call Toronto";
            return (
              <article
                key={r.label}
                data-testid={testid}
                className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-7 flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-flex w-11 h-11 rounded-full items-center justify-center"
                    style={{ background: "rgba(200,162,74,0.16)" }}
                  >
                    <PhoneCall size={18} className="text-[color:var(--maqam-navy)]" />
                  </span>
                  <div>
                    <p className="text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">
                      Regional Contact
                    </p>
                    <p className="font-serif text-xl text-[color:var(--maqam-navy)] leading-none mt-1">
                      {r.label.replace(" Regional Contact", "")}
                    </p>
                  </div>
                </div>
                <a
                  href={r.telHref}
                  data-testid={`${testid}-tel`}
                  className="mt-6 inline-flex items-center gap-2 font-semibold text-[color:var(--maqam-navy)] text-lg"
                >
                  <Phone size={18} className="text-[color:var(--maqam-gold)]" />
                  {r.displayPhone}
                </a>
                <a
                  href={r.telHref}
                  data-testid={`${testid}-cta`}
                  className="mt-6 btn-primary rounded-full px-5 py-3 font-semibold inline-flex items-center justify-center gap-2 w-full sm:w-auto"
                >
                  <PhoneCall size={16} /> {label}
                </a>
              </article>
            );
          })}
        </div>

        {/* WhatsApp — International Enquiries */}
        <article
          data-testid="whatsapp-callout"
          className="mt-6 review-shadow rounded-2xl overflow-hidden border border-[var(--maqam-border)] bg-white"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 items-stretch">
            <div className="md:col-span-3 p-7 md:p-9 flex flex-col justify-center">
              <div className="flex items-center gap-3">
                <WhatsAppIcon size={40} />
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">
                    Primary international contact
                  </p>
                  <p className="font-serif text-xl text-[color:var(--maqam-navy)] leading-none mt-1">
                    {CONTACT_INFO.whatsapp.label}
                  </p>
                </div>
              </div>
              <p className="mt-5 text-[var(--maqam-muted)] leading-relaxed max-w-lg">
                Many of our travellers reach us on WhatsApp — from Canada, the US, Australia, Kenya, Europe,
                Egypt, and beyond. Message us any time.
              </p>
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <a
                  href={CONTACT_INFO.whatsapp.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-white"
                  style={{ background: "#25D366" }}
                >
                  <WhatsAppIcon size={18} brand={false} className="text-white" />
                  Message Us on WhatsApp
                </a>
                <span className="inline-flex items-center gap-2 text-sm text-[var(--maqam-muted)]">
                  <Phone size={14} /> {CONTACT_INFO.whatsapp.displayNumber}
                </span>
              </div>
            </div>
            <div
              className="hidden md:block md:col-span-2 relative"
              style={{
                background:
                  "linear-gradient(140deg, #25D366 0%, #128C7E 100%)",
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center">
                <WhatsAppIcon size={112} brand={false} className="text-white/95" />
              </div>
            </div>
          </div>
        </article>

        {/* Email + Instagram + scheduling */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {/* Email */}
          <a
            href={`mailto:${CONTACT_INFO.email}`}
            data-testid="email-card"
            className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-6 flex flex-col"
          >
            <span
              className="inline-flex w-10 h-10 rounded-full items-center justify-center"
              style={{ background: "rgba(200,162,74,0.16)" }}
            >
              <Mail size={18} className="text-[color:var(--maqam-navy)]" />
            </span>
            <p className="mt-4 text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">Email</p>
            <p className="mt-1 font-semibold text-[color:var(--maqam-navy)]">{CONTACT_INFO.email}</p>
            <p className="mt-3 text-xs text-[var(--maqam-muted)] flex-1">Best for detailed enquiries and documents.</p>
          </a>

          {/* Instagram */}
          <a
            href={CONTACT_INFO.instagram.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-card"
            className="review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-6 flex flex-col"
          >
            <span
              className="inline-flex w-10 h-10 rounded-full items-center justify-center text-white"
              style={{
                background:
                  "linear-gradient(135deg, #F58529 0%, #DD2A7B 45%, #8134AF 75%, #515BD4 100%)",
              }}
            >
              <Instagram size={18} className="text-white" />
            </span>
            <p className="mt-4 text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">Instagram</p>
            <p className="mt-1 font-semibold text-[color:var(--maqam-navy)]">{CONTACT_INFO.instagram.handle}</p>
            <p className="mt-3 text-xs text-[var(--maqam-muted)] flex-1">Photos, reflections, and community moments.</p>
          </a>

          {/* Request a Callback */}
          <button
            type="button"
            onClick={() => scrollToForm("registration-form-section")}
            data-testid="book-a-call-card"
            className="text-left review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-6 flex flex-col cursor-pointer"
          >
            <span
              className="inline-flex w-10 h-10 rounded-full items-center justify-center"
              style={{ background: "rgba(200,162,74,0.16)" }}
            >
              <PhoneCall size={18} className="text-[color:var(--maqam-navy)]" />
            </span>
            <p className="mt-4 text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">Request a Callback</p>
            <p className="mt-1 font-semibold text-[color:var(--maqam-navy)]">Speak with our team</p>
            <p className="mt-3 text-xs text-[var(--maqam-muted)] flex-1">Send us your details and we'll call you back.</p>
          </button>

          {/* General Enquiries */}
          <button
            type="button"
            onClick={() => scrollToForm("contact-form-section")}
            data-testid="schedule-consultation-card"
            className="text-left review-shadow-hover rounded-2xl border border-[var(--maqam-border)] bg-white p-6 flex flex-col cursor-pointer"
          >
            <span
              className="inline-flex w-10 h-10 rounded-full items-center justify-center"
              style={{ background: "rgba(200,162,74,0.16)" }}
            >
              <CalendarClock size={18} className="text-[color:var(--maqam-navy)]" />
            </span>
            <p className="mt-4 text-xs uppercase tracking-[0.16em] text-[color:var(--maqam-gold-2)]">General Enquiries</p>
            <p className="mt-1 font-semibold text-[color:var(--maqam-navy)]">Request More Information</p>
            <p className="mt-3 text-xs text-[var(--maqam-muted)] flex-1">Community programs, seminars, or anything else — we'd love to hear from you.</p>
          </button>
        </div>

        {/* TICO / regulated-travel-advice disclosure */}
        <p
          data-testid="contact-tico-note"
          className="mt-8 text-xs text-[var(--maqam-muted)] leading-relaxed max-w-3xl"
        >
          Travel-specific questions, pricing, bookings, and package details will be handled by a registered travel advisor through our licensed travel partner, <span className="text-[color:var(--maqam-navy)] font-semibold">Amax Travel Canada, TICO Registration #50027257</span>.
        </p>
      </div>
    </section>
  );
}
